import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";
import moment from "moment";
import {
  accepted,
  black600,
  black700,
  blue300,
  cancelled,
  cinnabar300,
  curiousBlue300,
  green300,
  royalBlue300,
  white
} from "../../../theme/colors";
import { baseCaption } from "../../../theme/typography";
import { bold } from "../../../theme/typeface";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import { Button } from "../../../components/Button";
import { StatusTagStyle } from "../../../components/StatusTagStyle";
import getOutletActions from "../../../actions/outlet/getOutletActions";
import { getActionList } from "./helper/getActionList";
import { UpdateStatusModal } from "./modals/UpdateStatusModal";
import { WORKFLOW_KEYS } from "../../../helpers/constants/workflowKeys";
import ReactTooltip from "react-tooltip";
import { ToolTip } from "../../../components/ToolTip";
import { ACCEPT_OR_REJECT_FIELD_APP_CHANGES, GET_OUTLET_GROUP_SAVE, GET_SIMILAR_OUTLETS_ACTIONS, GET_CONSENT_FORM_PDF, RETRIGGERING_CONSENT, OUTLET_HOLD_API, GET_ENROLLMENT_FORM_PDF } from "../../../helpers/constants/apiUrl";
import { triggerSimpleAjax } from "../../../helpers/httpHelper";
import ImageGallery from "../../../components/image_gallery/ImageGallery";
import capitializeFirstChar from "../../../helpers/utils/capitializeFirstChar";
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from "@material-ui/icons/Close";
import themeSizes from "../../../theme/themeSizes";
import { SimilarOutletModal } from "./modals/SimilarOutletModal";
import { isMobileScreen } from "../../../helpers/isMobileScreen";

import { getCheckPendingRequestRetailerEdit } from "./getCheckPendingRequestRetailerEdit"
import { Error, Home } from "@material-ui/icons";
import { ActionButton, CampaignConfirmationModal } from "../components/CampaignConfirmationModal";
import getOutletData from "../../../actions/getOutletData";
import { DropDown } from "../../../components/DropDown";
// import { StartTimerModal } from "./modals/StartTimerModal";
import {
  isOutletHeaderShortcut,
  isUpdateStatusShortcut,
  getShortcutLabel,
} from "./helper/shortcutUtils";

const OutletMetaWrapper = styled.section`
  max-width: 100%;
  background: ${white};
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05), 0 0 0 1px rgb(0 0 0 / 0.05);
  min-height: 80px;
  padding: 24px;
  border-radius: 16px;
  position: relative;
  box-sizing: border-box;

  @media ${themeSizes.mobile} {
    padding: 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  position: relative;
  align-items: center;
  gap: 20px;

  @media ${themeSizes.tablet} {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding-right: 48px; /* Clear space for toggle on desktop/tablet */

  @media ${themeSizes.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding-right: 36px;
  }
`;

const HeaderInfoContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const MetaGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-top: 6px;

  @media ${themeSizes.mobile} {
    flex-direction: column;
    gap: 4px;
  }
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media ${themeSizes.mobile} {
    margin-left: 0 !important;
  }
`;

const MetaLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b; /* Slate 500 */
  margin-right: 4px;
`;

const MetaValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #334155; /* Slate 700 */
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  padding-right: 48px;

  @media ${themeSizes.tablet} {
    align-items: flex-start;
    width: 100%;
    padding-right: 0;
  }
`;

const HeaderRightButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  @media ${themeSizes.tablet} {
    width: 100%;
  }
`;

const WorkflowBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  box-sizing: border-box;
  
  ${props => {
    switch (props.state) {
      case "qc_pending":
      case "route_plan_pending":
      case "pending_audit":
        return css`
          background-color: #fffbeb; /* Amber 50 */
          color: #d97706; /* Amber 600 */
          border: 1px solid #fde68a; /* Amber 200 */
        `;
      case "qc_accepted":
      case "route_planned":
        return css`
          background-color: #ecfdf5; /* Emerald 50 */
          color: #059669; /* Emerald 600 */
          border: 1px solid #a7f3d0; /* Emerald 200 */
        `;
      case "data_ingested":
      case "field_ingested":
        return css`
          background-color: #eff6ff; /* Blue 50 */
          color: #2563eb; /* Blue 600 */
          border: 1px solid #bfdbfe; /* Blue 200 */
        `;
      default:
        return css`
          background-color: #f8fafc; /* Slate 50 */
          color: #475569; /* Slate 600 */
          border: 1px solid #e2e8f0; /* Slate 200 */
        `;
    }
  }}
`;

const StatusActionButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: ${props => props.isActive ? "pointer" : "not-allowed"};
  transition: all 0.2s ease;
  box-sizing: border-box;
  opacity: ${props => props.isActive ? 1 : 0.65};
  
  background-color: ${props => props.isActive ? "#10b981" : "#64748b"}; /* Emerald 500 or Slate 500 */
  
  &:hover {
    background-color: ${props => props.isActive ? "#059669" : "#64748b"};
  }
`;

const HoldBadgeButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  background-color: ${props => props.onHold ? "#fee2e2" : "#fef3c7"}; /* Red 100 or Amber 100 */
  color: ${props => props.onHold ? "#ef4444" : "#f59e0b"}; /* Red 500 or Amber 500 */
  border: 1px solid ${props => props.onHold ? "#fecaca" : "#fde68a"};
  
  &:hover {
    background-color: ${props => props.disabled ? "" : props.onHold ? "#fca5a5" : "#fcd34d"};
    color: ${props => props.disabled ? "" : props.onHold ? "#b91c1c" : "#d97706"};
  }
`;

const SimilarOutletsButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background-color: white;
  color: #10b981; /* Green 500 */
  border: 1px solid #10b981;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    background-color: #ecfdf5; /* Green 50 */
    border-color: #059669;
    color: #059669;
  }
`;

const PdfBadgeButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  height: 32px;
  min-width: 140px;
  white-space: nowrap;
  box-sizing: border-box;
  cursor: ${props => props.clickable ? "pointer" : "not-allowed"};
  transition: all 0.2s ease-in-out;
  
  background-color: ${props => props.bgColor || "#4770C1"};
  color: white;
  border: 1px solid ${props => props.bgColor || "#4770C1"};
  
  &:hover {
    ${props => props.clickable && css`
      background-color: white;
      color: ${props => props.bgColor || "#4770C1"};
    `}
  }
`;

const ExpandCollapseToggle = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #f1f5f9; /* Slate 100 */
  color: #475569; /* Slate 600 */
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background-color: #e2e8f0; /* Slate 200 */
    color: #0f172a; /* Slate 900 */
  }

  @media ${themeSizes.mobile} {
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
  }
`;

const ImageBlock = styled.div`
  width: 56px;
  height: 56px;
  background-image: url(${({ image }) => image});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  border: 1px solid #e2e8f0; /* Slate 200 */
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const HomeIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eff6ff; /* Blue 50 */
  border-radius: 12px;
  border: 1px solid #bfdbfe; /* Blue 200 */
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 24px;
  width: 100%;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: #f8fafc; /* Slate 50 */
  border: 1px solid #e2e8f0; /* Slate 200 */
  border-radius: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #cbd5e1; /* Slate 300 */
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05);
  }
`;

const SummaryLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748b; /* Slate 500 */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const SummaryValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a; /* Slate 900 */
  word-break: break-all;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  background: ${white};
  border: 1px solid #e2e8f0; /* Slate 200 */
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
`;

const Title = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a; /* Slate 900 */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #3b82f6; /* Blue 500 accent bottom border */
  display: inline-block;
  align-self: flex-start;
`;

const AssociatedBySpacerTitle = styled(Title)`
  visibility: hidden;
  @media ${themeSizes.mobile} {
    display: none;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748b; /* Slate 500 */
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1e293b; /* Slate 800 */
  word-break: break-all;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Block = styled.div`
  ${baseCaption}
  padding: 0;

  p:first-child {
    word-break: ${({ wordBreak }) => wordBreak || "break-all"};
  }

  ${(props) =>
    props.blockWidth &&
    css`
            width: ${props.blockWidth};

            p {
              font-weight: 500;
              font-size: 12px;
              line-height: 14px;
            }
          `}
`;

const LinkText = styled.span`
  ${baseCaption};
  color: ${curiousBlue300};
  cursor: pointer;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EditRowWrapper = styled.div`
  display: flex;

  p, h3 {
    flex: 33%;
  }
`;

export const OutletMeta = ({
  id,
  outletName,
  address,
  region,
  city,
  state,
  zip_code,
  created,
  workflow_permission,
  subProjectType,
  workflowOutletId,
  status,
  outlet_id,
  rsid,
  inbound_source,
  saveClickedData,
  setSaveClickedData,
  selectedOutletDetails = [],
  question_groups,
  getOutletDetails,
  history,
  related_workflow_object = {},
  setWorkflowObjectActionConfig = () => {
  },
  selectedCallingTrackerReasonDetails = {},
  callingTrackerList = [],
  resetCallingtrackerResonDetails = () => {
  },
  triggerNotification,
  updateActionListAvailable,
  banner_image,
  setWorkflowStateChanged = () => {
  },
  workflowStateChanged,
  field_survey_start_datetime = new Date(),
  field_survey_end_datetime,
  field_survey_start_latitude,
  field_survey_end_latitude,
  field_survey_start_longitude,
  field_survey_end_longitude,
  notes_by_field_auditor,
  field_survey_start_location_accuracy,
  previous_address,
  distributor_gst_number,
  distributor_gst_trade_name,
  distributor_gst_legal_name,
  phone_number1,
  phone_number2,
  previous_phone_number1,
  previous_phone_number2,
  slug,
  qc_display_notes,
  calling_display_notes,
  client_remarks,
  calling_remarks,
  name,
  survey_not_done_notes_by_field_auditor,
  bulk_created_by,
  bulk_approved_by,
  beneficiary_id: propBeneficiaryId,
  statusUpdateCallback,
  is_hold,
  startTimerCallback,
  mid_survey_termination,
  mid_survey_termination_reason,
}) => {
  const [isOnHold, setIsOnHold] = useState(is_hold || false)
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const [isEditViewEnable, setEditViewEnable] = useState(false);
  const [openUpdateModal, setUpdateModalOpen] = useState(false);
  const [actionList, setActionList] = useState([]);
  const [galleryImage, setGalleryImage] = useState([]);
  const [expand, setExpand] = useState(false)
  const [openSimilarOutletModal, setSimilarOutletModal] = useState(false);
  const [similarOutletData, setSimilarOutletData] = useState([]);
  const [isRetailerRequestPending, setIsRetailerRequestPending] = useState(false);
  const [countRetailerRequestPending, setCountRetailerRequestPending] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [statusColor, setStatusColor] = useState("#6c757d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [apiMessage, setApiMessage] = useState("");
  const [consentName, setconsentName] = useState("");
  const [enrollmentFormData, setEnrollmentFormData] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [refeshResendConsent, setrefeshResendConsent] = useState(false);
  const [holdError, setHoldError] = useState("");
  // const [showStartTimerModal, setShowStartTimerModal] = useState(false);
  const currentUser = useSelector((state) => state.user?.user_details);
  const [enrollmentPdfData, setEnrollmentPdfData] = useState(null);
  const [enrollmentStatusColor, setEnrollmentStatusColor] = useState("#6c757d");
  const [enrollmentStatus, setEnrollmentStatus] = useState("");
  const [showEnrollmentButton, setShowEnrollmentButton] = useState(false);
  const [enrollmentApiMessage, setEnrollmentApiMessage] = useState("");
  const [enrollmentReason, setEnrollmentReason] = useState("");
  const [showEnrollmentReasonModal, setShowEnrollmentReasonModal] = useState(false);
  const [enrollmentInputReason, setEnrollmentInputReason] = useState("");
  const isMobile = isMobileScreen();
  const {
    assigned_field_manager,
    assigned_qc_manager,
    assigned_calling_executive,
    assigned_field_auditor,
    assigned_field_coordinator,
    assigned_iftu_executive,
    assigned_qc_executive,
    assigned_calling_manager,
    field_audit_end_date,
    field_audit_start_date,
    overall_remarks,
    rsa_verification_status,
    rsa_photo_proofs,
    other_config,
    workflow_state,
    client_given_phone_number,
    beneficiary_id,
    field_survey_mobile_version_number
  } = related_workflow_object || {};


  const HOLD_REASONS = [
    { label: "Account Validation Issue", value: "account_validation_issue" },
    { label: "IT Issue", value: "it_issue" },
    { label: "Requested by IFTU Team", value: "requested_by_iftu_team" },
  ];

  const handleHoldSubmit = () => {
    if (!selectedReason || selectedReason.trim() === "") {
      setHoldError("Please select a hold reason");
      return;
    }

    setHoldError("");
    submitHold(selectedReason);
  };
  const submitHold = (reason) => {
    triggerSimpleAjax(
      OUTLET_HOLD_API,
      "POST",
      {
        is_hold: true,
        outlet_ids: [id],
        reason: reason,
      },
      () => {
        setIsOnHold(true);
        setShowHoldModal(false);
        setSelectedReason("");
        setCustomReason("");
        triggerNotification({
          messageText: "Outlet put on hold successfully",
          status: "SUCCESS",
        });
        getOutletDetails();
      },
      (err) => {
        triggerNotification({
          messageText: "Failed to put outlet on hold",
          status: "FAILED",
        });
        console.log(err);
      }
    );
  };
  useEffect(() => {
    setIsOnHold(is_hold || false);
  }, [is_hold]);

  const handleUnhold = () => {
    triggerSimpleAjax(
      OUTLET_HOLD_API,
      "POST",
      {
        is_hold: false,
        outlet_ids: [id],
      },
      () => {
        triggerNotification({
          messageText: "Outlet resumed successfully",
          status: "SUCCESS",
        });

        setIsOnHold(false);
        // setShowStartTimerModal(true);
        getOutletDetails();
      }
    );
  };
  const parseHoldStatus = (data) => {
    return data?.is_hold ?? false;
  };
  const [isWorkFlow, setWorkFlow] = useState(true)

  const checkPendingRequest = async () => {
    const isAllowed = actionList.length
      ? isCallingExecutive
        ? enableCheckForEmptyTracker
          ? disableButton !== true
          : false
        : disableButton !== true
      : false;

    if (!isAllowed && !enbaleUpdateStatusWithoutEditable) {
      return;
    }

    try {
      const workflow_object_id =
        workflowOutletId ||
        related_workflow_object?.id ||
        related_workflow_object?.workflow_object ||
        selectedOutletDetails?.[0]?.workflowOutletId ||
        history?.location?.state?.workflowOutletId ||
        outlet_id ||
        "";

      if (!workflow_object_id) {
        setUpdateModalOpen(true);
        return;
      }

      const responseRequestPending = await getCheckPendingRequestRetailerEdit(workflow_object_id);

      if (responseRequestPending.pending_request_exist === false) {
        setUpdateModalOpen(true);
      }
      else if (responseRequestPending.pending_request_exist === true) {
        setIsRetailerRequestPending(true);
        setCountRetailerRequestPending(responseRequestPending.count_pending_request);
        setShowPopup(true);
      }

    } catch (error) {
      console.log("Error fetching request data:", error);
      setUpdateModalOpen(true);
    }
  };

  const handleCardOpenAndClose = () => {
    setExpand(!expand);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent shortcut if any modal/dialog is open
      if (
        openUpdateModal ||
        showHoldModal ||
        showPopup ||
        openSimilarOutletModal ||
        showEnrollmentReasonModal ||
        // showStartTimerModal ||
        (galleryImage && galleryImage.length > 0)
      ) {
        return;
      }

      if (isOutletHeaderShortcut(e)) {
        e.preventDefault();
        e.stopPropagation();
        setExpand((prev) => !prev);
      } else if (isUpdateStatusShortcut(e)) {
        e.preventDefault();
        e.stopPropagation();
        const isUpdateActive =
          (showUpdateStatus || enbaleUpdateStatusWithoutEditable) &&
          (actionList.length
            ? isCallingExecutive
              ? enableCheckForEmptyTracker
                ? disableButton !== true
                : false
              : disableButton !== true
            : false);

        if (isUpdateActive) {
          checkPendingRequest();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    expand,
    openUpdateModal,
    showHoldModal,
    showPopup,
    openSimilarOutletModal,
    showEnrollmentReasonModal,
    // showStartTimerModal,
    galleryImage,
    showUpdateStatus,
    enbaleUpdateStatusWithoutEditable,
    actionList,
    isCallingExecutive,
    enableCheckForEmptyTracker,
    disableButton,
    workflowOutletId,
    related_workflow_object,
    selectedOutletDetails,
    history,
    outlet_id,
  ]);

  const regionName = (region || {}).name;
  const cityName = (city || {}).name;
  const stateName = (state || {}).name;
  const addressElements = [
    address,
    regionName,
    cityName,
    stateName,
  ].filter((element) => Boolean(element));

  const showUpdateStatus = [
    "calling-executive-workflow-permission",
    "qc-executive-workflow-permission",
    "qc-manager-workflow-permission",
  ].includes(workflow_permission);

  const enbaleUpdateStatusWithoutEditable =
    WORKFLOW_KEYS.CLIENT_MANAGER === workflow_permission ||
    (related_workflow_object || {}).type_of_calling === "intimation";

  const isCallingExecutive =
    WORKFLOW_KEYS.CALLING_EXECUTIVE === workflow_permission;

  const isClientManager = WORKFLOW_KEYS.CLIENT_MANAGER === workflow_permission || WORKFLOW_KEYS.CLIENT_EXECUTIVE === workflow_permission;
  const isQCExecutive = WORKFLOW_KEYS.QC_EXECUTIVE === workflow_permission;
  const isQcAccepted = workflow_state != "qc_accepted"

  const showSimilarOutlet =
    subProjectType === 'payout_cycle' || subProjectType === 'kyc' ? true : false;

  const uploadCallingTrackerDetails = () => {
    triggerSimpleAjax(
      GET_OUTLET_GROUP_SAVE,
      "POST",
      {
        object_type: "workflow_calling_tracker",
        data_to_create: null,
        data_to_update: selectedCallingTrackerReasonDetails,
      },
      (response) => {
        resetCallingtrackerResonDetails({});
      },
      (error) => {
        console.log(error);
      }
    );
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    triggerSimpleAjax(
      GET_CONSENT_FORM_PDF(slug),
      "GET",
      {},
      (response) => {
        if (response?.result) {
          const data = response.result;
          setPdfData(data);
          setApiMessage(response.message);

          switch (data.code) {
            case 1:
              setStatusColor("#28a745");
              setconsentName("Show Consent");
              setShowButton(true);
              break;
            case 2:
            case 4:
              setStatusColor("#6c757d");
              setconsentName("Consent Pending");
              setShowButton(true);
              break;
            case 3:
              setStatusColor(cancelled);
              setconsentName("Consent Rejected");
              setShowButton(true);
              break;
            case 5:
            case 6:
              setShowButton(false);
              break;
            default:
              setStatusColor("#6c757d");
              setconsentName("Consent Pending");
              setShowButton(true);
          }

          if (!data.pdf_url?.trim() && data.code !== 3) {
            setStatusColor("#6c757d");
          }
        } else {
          setError("No PDF found");
          setStatusColor("#6c757d");
          setconsentName("Consent Pending");
          setShowButton(true);
        }
        setLoading(false);
      },
      (err) => {

        setError("Failed to fetch consent form");
        setStatusColor("#6c757d");
        setShowButton(true);
        setLoading(false);
      }
    );
  }, [slug, refeshResendConsent]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    triggerSimpleAjax(
      GET_ENROLLMENT_FORM_PDF(slug),
      "GET",
      {},
      (response) => {
        if (response?.result) {
          const data = response.result;
          setEnrollmentPdfData(data);
          setEnrollmentApiMessage(response.message);

          switch (data.code) {
            case 1:
              // Accepted
              setEnrollmentStatusColor("#28a745"); // Green
              setEnrollmentStatus("Enrollment Accepted");
              setShowEnrollmentButton(true);
              break;
            case 2:
              setEnrollmentStatusColor("#0859a0");
              setEnrollmentStatus("Enrollment Pending");
              setEnrollmentReason(data.reason || "");
              setShowEnrollmentButton(true);
              break;
            case 3:
              setEnrollmentStatusColor("#fdb314"); // Orange
              setEnrollmentStatus("Enrollment Refused");
              setEnrollmentReason(data.reason || "");
              setShowEnrollmentButton(true);
              break;
            case 4:
              setEnrollmentStatusColor("#dc3545"); // Red
              setEnrollmentStatus("Enrollment Rejected");
              setEnrollmentReason(data.reason || "");
              setShowEnrollmentButton(true);
              break;
            default:
              setEnrollmentStatusColor("#6c757d");
              setEnrollmentStatus("Enrollment Pending");
              setShowEnrollmentButton(false);
          }
        } else {
          setEnrollmentStatusColor("#6c757d");
          setEnrollmentStatus("Enrollment Pending");
          setShowEnrollmentButton(false);
        }
      },
      (err) => {
        console.log("Failed to fetch enrollment status:", err);
        setEnrollmentStatusColor("#6c757d");
        setEnrollmentStatus("Enrollment Pending");
        setShowEnrollmentButton(false);
      }
    );
  }, [slug]);


  const Call_Api_Retriggering_Consent = () => {

    triggerSimpleAjax(
      RETRIGGERING_CONSENT,
      "POST",
      {
        outlet_ids: [id],
      },
      (response) => {
        if (response.code == 200 && response.success == true) {
          setrefeshResendConsent(true)
        } else {
          // const invalidMessage =
          //   response?.result?.invalid_outlets?.[0]?.message;


          triggerNotification({
            messageText: response.message,
          });
        }
      },
      (error) => {
        triggerNotification({
          messageText: error?.message,
        });
      }
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (workflow_state === "qc_accepted") {
      setWorkFlow(true)
    } else if (workflow_state === "wip_client_accepted") {
      setWorkFlow(true)
    } else if (workflow_state === "calling_accepted") {
      setWorkFlow(true)
    }


    setEditViewEnable(workflow_state === "qc_pending" && isQCExecutive && (previous_phone_number2 != null || previous_phone_number1 != null || previous_address != null))


    // if (showSimilarOutlet) {


    triggerSimpleAjax(
      GET_SIMILAR_OUTLETS_ACTIONS(id),
      "GET",
      {},
      (response) => {

        if (Array.isArray(response) && response.length === 0) {
          setSimilarOutletData(null)
        } else {
          setSimilarOutletData(response)
        }
      },
      (err) => {
        console.log(err)
      })

    // }
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (showUpdateStatus || enbaleUpdateStatusWithoutEditable) {
      getOutletActions(id)
        .then((res) => {
          const {
            allowed_user_actions_config,
            allowed_user_special_actions_config,
          } = res;
          setWorkflowObjectActionConfig(res);
          const actions = {
            allowed_user_actions_config: Array.isArray(
              allowed_user_actions_config
            )
              ? allowed_user_actions_config[0]
              : allowed_user_actions_config,
            allowed_user_special_actions_config:
              allowed_user_special_actions_config || {},
          };
          const optionList = getActionList({
            actions,
            selectedOutletDetails,
            checkSpecialAction: false,
          });
          setActionList(optionList);
          updateActionListAvailable(!!optionList.length);
        })
        .catch((error) => console.log(error));
    }
  }, [showUpdateStatus, enbaleUpdateStatusWithoutEditable]);

  const addressText = addressElements.reduce(
    (acc, text, index) =>
      text
        ? `${acc}${text}${index !== addressElements.length - 1 ? "," : ""}`
        : acc,
    ""
  );

  const formatDate = (date) => {
    let travelTime = moment(date).add({ hours: 5, minutes: 30 }).format('DD-MMM-YYYY hh:mm A')

    let tmp_date = travelTime ? moment(travelTime).format("DD-MMM-YYYY hh:mm A")
      .split(" ") : "N/A"
    if (tmp_date === "N/A") {
      return "N/A"
    }
    return `${tmp_date[0].replace("-", " ")} | ${tmp_date.slice(1)} `.replace(",", " ")
  }
  const formatDateUpdatedTime = (date) => {
    let tmp_date = date ? moment(date).format("DD-MMM-YYYY hh:mm A")
      .split(" ") : "N/A"
    if (tmp_date === "N/A") {
      return "N/A"
    }
    return `${tmp_date[0].replace("-", " ")} | ${tmp_date.slice(1)} `.replace(",", " ")
  }

  // const MilliToMinSec = (millis) => {
  //   var minutes = Math.floor(millis / 60000);
  //   var seconds = ((millis % 60000) / 1000).toFixed(0);
  //   return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
  // }

  const handlePlural = (val, string) => {
    return (val === 1 ? `${val} ${string}` : `${val} ${string}s`)
  }



  function msToDuration(ms) {
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    if (days === 0 && hours === 0 && minutes === 0) {
      return `${handlePlural(seconds, 'second')}`
    } else if (days === 0 && hours === 0) {
      return `${handlePlural(minutes, 'minute')}`
    } else if (days === 0) {
      return `${handlePlural(hours, 'hour')} ${handlePlural(minutes, 'minute')}`
    } else {
      return `${handlePlural(days, 'day')} ${handlePlural(hours, 'hour')} ${handlePlural(minutes, 'minute')}`
    }
  }

  const duration = (startDate, endDate) => {
    startDate = new Date(startDate)
    endDate = new Date(endDate)
    let duration = "N/A";
    if (startDate && endDate) {
      duration = (endDate.getTime() - startDate.getTime());
      return msToDuration(duration)
    }
    return duration
  }

  const handleMetaEdit = (value) => {
    triggerSimpleAjax(
      ACCEPT_OR_REJECT_FIELD_APP_CHANGES(slug),
      "POST",
      {
        "action_to_perform": value
      },
      (res) => {
        window.location.reload()
      },
    )
  }

  const isQCPendingWorkflow =
    workflow_state === "qc_pending";

  const isQCExecutiveAssigned = Boolean(
    assigned_qc_executive && currentUser && (assigned_qc_executive.id === currentUser.id || assigned_qc_executive.username === currentUser.username)
  );

  const canEditHoldButton =
    isQCPendingWorkflow &&
    [
      WORKFLOW_KEYS.QC_EXECUTIVE,
      WORKFLOW_KEYS.QC_MANAGER
    ].includes(workflow_permission) &&
    isQCExecutiveAssigned;
  const disableFields = is_hold;

  const selectedCallingTrackerReasonDetailsKeys = Object.keys(
    selectedCallingTrackerReasonDetails
  );

  const isCallingTrackerDetailsAvailable = selectedCallingTrackerReasonDetailsKeys.length
    ? !!selectedCallingTrackerReasonDetailsKeys.filter((key) =>
      selectedCallingTrackerReasonDetails[key].reason
        .toLowerCase()
        .includes("successful")
    ).length ||
    selectedCallingTrackerReasonDetailsKeys.length ===
    callingTrackerList.length
    : false;
  const enableCheckForEmptyTracker = callingTrackerList.length
    ? isCallingTrackerDetailsAvailable
    : true;
  let rsa_photo_proof =
    rsa_photo_proofs
      ? rsa_photo_proofs.map((item, inx) => {
        return item.image;
      })
      : [];

  const disableButton = saveClickedData ? !workflowStateChanged
    ? !enbaleUpdateStatusWithoutEditable
      ? actionList?.length || Object.values(saveClickedData)?.length
        ? Object.values(saveClickedData)?.length === question_groups?.length
          ? Object.values(saveClickedData).filter((data) => !Boolean(data))
            ?.length
          : true
        : true
      : false
    : true : null;

  const EditElement = ({ name, pkey, previous_key }) => (<EditRowWrapper>
    <p>{name}</p>
    <p>{previous_key}</p>
    <p>{pkey}</p>
  </EditRowWrapper>)




  if (actionList.length) {
    if (isCallingExecutive) {
      if (enableCheckForEmptyTracker) {
        console.log(`button Status >> 1 >> ${disableButton}`)

      }
    }
  }
  return (
    <Fragment>
      <OutletMetaWrapper expand={expand}>
        <ExpandCollapseToggle
          onClick={handleCardOpenAndClose}
          title={`Outlet Header (${getShortcutLabel("OUTLET_HEADER")})`}
          data-tip
          data-for="outlet-header-toggle"
        >
          {expand ? <CloseIcon /> : <MenuIcon />}
        </ExpandCollapseToggle>
        <ReactTooltip id="outlet-header-toggle" place="left" effect="solid">
          Outlet Header ({getShortcutLabel("OUTLET_HEADER")})
        </ReactTooltip>

        <div style={{ display: 'flex', flexDirection: "column" }}>
          <HeaderRow>

            <HeaderLeft>
              {(banner_image || {}).image ? (
                <ImageBlock
                  image={(banner_image || {}).image}
                  onClick={() => {
                    setGalleryImage([banner_image.image])
                  }}
                />
              ) : (
                <HomeIconWrapper>
                  <Home style={{ fontSize: 28, color: '#3b82f6' }} />
                </HomeIconWrapper>
              )}

              <HeaderInfoContainer>
                <h3 style={{ margin: '0 0 6px 0', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  {name}
                </h3>

                <MetaGrid>
                  <MetaColumn>
                    <span>
                      <MetaLabel>RSID:</MetaLabel>
                      <MetaValue>{rsid || "N/A"}</MetaValue>
                    </span>
                    <span>
                      <MetaLabel>Survey Start Date:</MetaLabel>
                      <MetaValue>{moment(field_survey_start_datetime).format('DD MMM-YYYY | h:mm:ss A')}</MetaValue>
                    </span>
                  </MetaColumn>

                  <MetaColumn className="second-column">
                    <span>
                      <MetaLabel>Outlet ID:</MetaLabel>
                      <MetaValue>{outlet_id || "N/A"}</MetaValue>
                    </span>
                    <span>
                      {isClientManager == false && (
                        <>
                          <MetaLabel>Survey End Date:</MetaLabel>
                          <MetaValue>{moment(field_survey_end_datetime).format('DD MMM-YYYY | h:mm:ss A')}</MetaValue>
                        </>
                      )}
                    </span>
                  </MetaColumn>
                </MetaGrid>

                {showHoldModal && (
                  <div style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: "white",
                      padding: 20,
                      borderRadius: 10,
                      width: 350,
                    }}>
                      <h3 style={{ marginTop: 0 }}>Hold Reason</h3>

                      <select
                        value={selectedReason}
                        onChange={(e) => {
                          setSelectedReason(e.target.value);
                          setHoldError("");
                        }}
                        style={{
                          width: "100%",
                          marginTop: 10,
                          padding: "8px 12px",
                          borderRadius: 6,
                          border: holdError ? "1px solid #ff0019" : "1px solid #ddd",
                          fontFamily: "inherit",
                          fontSize: "14px",
                          outline: "none"
                        }}
                      >
                        <option value="">Select a reason</option>

                        {HOLD_REASONS.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </select>
                      {holdError && (
                        <div
                          style={{
                            color: "#ff0019",
                            fontSize: "12px",
                            marginTop: "6px",
                            fontWeight: 500
                          }}
                        >
                          {holdError}
                        </div>
                      )}

                      <div style={{
                        marginTop: 20,
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10
                      }}>
                        <button
                          style={{
                            background: "#f8f9fa",
                            border: "1px solid #ddd",
                            padding: "8px 16px",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500"
                          }}
                          onClick={() => {
                            setShowHoldModal(false);
                            setSelectedReason("");
                            setCustomReason("");
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          style={{
                            background: "#28a745",
                            color: "white",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500"
                          }}
                          onClick={handleHoldSubmit}
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </HeaderInfoContainer>
            </HeaderLeft>

            {/* {showStartTimerModal && (
              <StartTimerModal
                isModelVisible={showStartTimerModal}
                onProceedClick={() => {
                  setShowStartTimerModal(false);
                  if (startTimerCallback) {
                    startTimerCallback();
                  }
                }}
              />
            )} */}

            <HeaderRight>
              <HeaderRightButtons>
                {workflow_state ? (
                  <WorkflowBadge state={workflow_state}>
                    <span>Workflow Status:</span>
                    <strong>{workflow_state.split('_').map(a => capitializeFirstChar(a)).join(' ')}</strong>
                  </WorkflowBadge>
                ) : null}

                {showUpdateStatus || enbaleUpdateStatusWithoutEditable ? (
                  <ToolTip
                    content={actionList.length ? "" : " No action possible!"}
                    align="left center"
                    padding="4px"
                    contentWidth="150px"
                    rad
                    icon={
                      <StatusActionButton
                        isActive={
                          actionList.length
                            ? isCallingExecutive
                              ? enableCheckForEmptyTracker
                                ? disableButton !== true
                                : false
                              : disableButton !== true
                            : false
                        }
                        title={`Update Status (${getShortcutLabel("UPDATE_STATUS")})`}
                        onClick={() => {
                          const isAllowed = actionList.length
                            ? isCallingExecutive
                              ? enableCheckForEmptyTracker
                                ? disableButton !== true
                                : false
                              : disableButton !== true
                            : false;
                          if (isAllowed || enbaleUpdateStatusWithoutEditable) {
                            checkPendingRequest();
                          }
                        }}>
                        Update Status
                      </StatusActionButton>
                    }
                  />
                ) : null}

                {(enbaleUpdateStatusWithoutEditable || (showUpdateStatus && status)) ? (
                  <StatusTagStyle color={black700} marginLeft="0" marginRight="0" size="small">
                    {status}
                  </StatusTagStyle>
                ) : null}

                {/* {(isOnHold ? (
                  <HoldBadgeButton
                    onHold={true}
                    disabled={!canEditHoldButton}
                    onClick={() => {
                      if (canEditHoldButton) {
                        handleUnhold();
                      }
                    }}
                  >
                    Unhold
                  </HoldBadgeButton>
                ) : (
                  <HoldBadgeButton
                    onHold={false}
                    disabled={!canEditHoldButton}
                    onClick={() => {
                      if (canEditHoldButton) {
                        setShowHoldModal(true);
                      }
                    }}
                  >
                    Hold
                  </HoldBadgeButton>
                ))} */}

                {showSimilarOutlet ? (
                  <ToolTip
                    align="top center"
                    padding="4px"
                    contentWidth="110px"
                    icon={
                      <SimilarOutletsButton
                        onClick={() => {
                          setSimilarOutletModal(true)
                        }}>
                        Similar Outlets - {similarOutletData?.length || 0}
                      </SimilarOutletsButton>
                    }
                  />
                ) : null}
              </HeaderRightButtons>

              <Block
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "8px",
                  padding: 0,
                  marginLeft: 0,
                  marginRight: 0,
                  flexWrap: "wrap",
                  whiteSpace: "nowrap"
                }}
              >
                {showButton && (
                  <Block style={{ padding: 0, margin: 0 }}>
                    <ToolTip
                      content={
                        pdfData?.code === 1
                          ? "Open Consent Form!"
                          : "PDF can’t be opened for this status"
                      }
                      align="top center"
                      padding="4px"
                      contentWidth="140px"
                      rad
                      icon={
                        <PdfBadgeButton
                          clickable={pdfData?.code === 1 || pdfData?.code === 3}
                          bgColor={statusColor}
                          onClick={() => {
                            if (pdfData?.code === 1 && pdfData?.pdf_url) {
                              window.open(pdfData.pdf_url, "_blank");
                            } else if (pdfData?.code === 3) {
                              setShowPopup(true);
                            }
                          }}
                        >
                          {consentName}
                        </PdfBadgeButton>
                      }
                    />
                  </Block>
                )}

                {showEnrollmentButton && (
                  <Block style={{ padding: 0, margin: 0 }}>
                    <ToolTip
                      content={
                        enrollmentPdfData?.code === 1
                          ? "Open Enrollment Form!"
                          : enrollmentPdfData?.reason ||
                          "PDF can’t be opened for this status"
                      }
                      align="top center"
                      padding="4px"
                      contentWidth="140px"
                      rad
                      icon={
                        <PdfBadgeButton
                          clickable={enrollmentPdfData?.code === 1}
                          bgColor={enrollmentStatusColor}
                          onClick={() => {
                            if (
                              enrollmentPdfData?.code === 1 &&
                              enrollmentPdfData?.pdf_url
                            ) {
                              window.open(
                                enrollmentPdfData.pdf_url,
                                "_blank"
                              );
                            } else if (
                              enrollmentPdfData?.code === 3 ||
                              enrollmentPdfData?.code === 4
                            ) {
                              setEnrollmentInputReason(
                                enrollmentPdfData?.reason || ""
                              );
                              setShowEnrollmentReasonModal(true);
                            }
                          }}
                        >
                          {enrollmentStatus}
                        </PdfBadgeButton>
                      }
                    />
                  </Block>
                )}

                {(mid_survey_termination === true || mid_survey_termination === "True") && (
                  <Block style={{ padding: 0, margin: 0 }}>
                    <ToolTip
                      content={
                        mid_survey_termination_reason
                          ? `Reason: ${mid_survey_termination_reason}`
                          : "Mid Survey Termination"
                      }
                      align="top center"
                      padding="4px"
                      contentWidth="180px"
                      rad
                      icon={
                        <div
                          style={{
                            backgroundColor: "#fff",
                            color: "#dd1212",
                            border: "1px solid #dd1212",
                            borderRadius: "20px",
                            padding: "6px 18px",
                            height: "34px",
                            minWidth: "180px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            whiteSpace: "nowrap",
                            boxSizing: "border-box",
                            cursor: "default",
                            fontSize: "14px",
                            fontWeight: "400",
                            fontFamily: "Arial, sans-serif",
                          }}
                        >
                          Mid Survey Termination
                        </div>
                      }
                    />
                  </Block>
                )}
              </Block>
            </HeaderRight>
          </HeaderRow>

          {expand && (
            <Fragment>
              <SummaryRow>
                <SummaryItem>
                  <SummaryLabel>Duration</SummaryLabel>
                  <SummaryValue>
                    {
                      field_survey_start_datetime && field_survey_end_datetime ?
                        duration(field_survey_start_datetime, field_survey_end_datetime) : "N/A"
                    }
                  </SummaryValue>
                </SummaryItem>

                <SummaryItem>
                  <SummaryLabel>Upload Date & Time</SummaryLabel>
                  <SummaryValue>
                    {formatDateUpdatedTime(created)}
                  </SummaryValue>
                </SummaryItem>

                <SummaryItem>
                  <SummaryLabel>Source</SummaryLabel>
                  <SummaryValue>
                    {capitializeFirstChar((inbound_source || "").replace(/_/g, " "))}
                  </SummaryValue>
                </SummaryItem>

                <SummaryItem>
                  <SummaryLabel>RSA Verification Status</SummaryLabel>
                  <SummaryValue style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>
                      {capitializeFirstChar(
                        (rsa_verification_status || "").replace(/_/g, " ")
                      ) || "N/A"}
                    </span>
                    {(rsa_photo_proof || []).length ? (
                      <LinkText onClick={() => {
                        setGalleryImage([`${rsa_photo_proof}`])
                      }}>
                        View photos
                      </LinkText>
                    ) : null}
                  </SummaryValue>
                </SummaryItem>
              </SummaryRow>

              <DetailRow>
                <DetailBlock>
                  <Title>GST Details</Title>
                  <InfoList>
                    <InfoItem>
                      <InfoLabel>Distributor GST Number</InfoLabel>
                      <InfoValue>{distributor_gst_number || "N/A"}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Distributor GST Trade Name</InfoLabel>
                      <InfoValue>{distributor_gst_trade_name || "N/A"}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Distributor GST Legal Name</InfoLabel>
                      <InfoValue>{distributor_gst_legal_name || "N/A"}</InfoValue>
                    </InfoItem>
                  </InfoList>
                </DetailBlock>

                <DetailBlock>
                  <Title>Calling Details</Title>
                  <InfoList>
                    <InfoItem>
                      <InfoLabel>Calling Status</InfoLabel>
                      <InfoValue>{related_workflow_object.calling_display_notes || "-"}</InfoValue>
                    </InfoItem>
                    {(isClientManager == false || (isClientManager == true && isQcAccepted == true)) && (
                      <InfoItem>
                        <InfoLabel>Calling Notes</InfoLabel>
                        <InfoValue>{calling_remarks || "-"}</InfoValue>
                      </InfoItem>
                    )}
                  </InfoList>
                </DetailBlock>
              </DetailRow>

              <DetailRow>
                <DetailBlock>
                  <Title>Location</Title>
                  <InfoList>
                    <InfoItem>
                      <InfoLabel>Start Location</InfoLabel>
                      <InfoValue>
                        {field_survey_start_latitude && field_survey_start_longitude ?
                          `${field_survey_start_latitude}, ${field_survey_start_longitude}` : "N/A"}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>End Location</InfoLabel>
                      <InfoValue>
                        {field_survey_end_latitude && field_survey_end_longitude ?
                          `${field_survey_end_latitude}, ${field_survey_end_longitude}` : "N/A"}
                      </InfoValue>
                    </InfoItem>
                    {isClientManager == false && (
                      <InfoItem>
                        <InfoLabel>Location Accuracy</InfoLabel>
                        <InfoValue>{field_survey_start_location_accuracy || "N/A"}</InfoValue>
                      </InfoItem>
                    )}
                  </InfoList>
                </DetailBlock>

                {(isClientManager == false || (isClientManager == true && isQcAccepted == true)) && (
                  <DetailBlock>
                    <Title>Other Details</Title>
                    <InfoList>
                      <InfoItem>
                        <InfoLabel>Pre Survey Remark</InfoLabel>
                        <InfoValue>
                          {workflow_state ?
                            (workflow_state === "wip_client_accepted" ||
                              workflow_state === "qc_accepted" ||
                              workflow_state === "calling_accepted") ?
                              isClientManager == true ? "-" : survey_not_done_notes_by_field_auditor || "-"
                              : survey_not_done_notes_by_field_auditor || "-"
                            : survey_not_done_notes_by_field_auditor || "-"
                          }
                        </InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Notes By Auditor</InfoLabel>
                        <InfoValue>
                          {notes_by_field_auditor ? (
                            notes_by_field_auditor.length > 22 ? (
                              <>
                                {notes_by_field_auditor.substring(20, 0) + "..."}
                                <LinkText data-tip data-for={`read-more-notes-auditor`}>
                                  {"  "}
                                  Read More
                                </LinkText>
                                <ReactTooltip id={`read-more-notes-auditor`} place={"bottom"} type={"dark"}>
                                  <Block blockWidth={"250px"}>
                                    {notes_by_field_auditor}
                                  </Block>
                                </ReactTooltip>
                              </>
                            ) : notes_by_field_auditor
                          ) : "-"
                          }
                        </InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Notes</InfoLabel>
                        <InfoValue>{qc_display_notes || "-"}</InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>Client Notes</InfoLabel>
                        <InfoValue>{client_remarks || "-"}</InfoValue>
                      </InfoItem>
                    </InfoList>
                  </DetailBlock>
                )}
              </DetailRow>

              <DetailRow>
                <DetailBlock>
                  <Title>Contact Details</Title>
                  <InfoList>
                    <InfoItem>
                      <InfoLabel>Phone Number 1</InfoLabel>
                      <InfoValue>{previous_phone_number1 ? previous_phone_number1 : phone_number1 || "N/A"}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Phone Number 2</InfoLabel>
                      <InfoValue>{previous_phone_number2 ? previous_phone_number2 : phone_number2 || "N/A"}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Client Phone Number</InfoLabel>
                      <InfoValue>{client_given_phone_number || "N/A"}</InfoValue>
                    </InfoItem>
                  </InfoList>
                </DetailBlock>

                <DetailBlock>
                  <Title>Address</Title>
                  <InfoList>
                    <InfoItem>
                      <InfoLabel>Outlet Name</InfoLabel>
                      <InfoValue style={{ fontWeight: 600 }}>{outletName || "N/A"}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Address Details</InfoLabel>
                      <InfoValue>
                        <LocationOnIcon style={{ color: '#3b82f6', fontSize: 18, marginRight: 2 }} />
                        <span>{previous_address ? previous_address : addressText} {zip_code}</span>
                      </InfoValue>
                    </InfoItem>
                  </InfoList>
                </DetailBlock>
              </DetailRow>

              {isClientManager == false && (
                <DetailRow>
                  <DetailBlock style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                    <div>
                      <Title style={{ borderBottomColor: '#10b981' }}>Associated By</Title>
                      <InfoList>
                        <InfoItem>
                          <InfoLabel>Field Coordinator</InfoLabel>
                          <InfoValue>{(assigned_field_coordinator || {}).name || "N/A"}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Field Auditor</InfoLabel>
                          <InfoValue>
                            {(assigned_field_auditor || {}).name || "N/A"}{" "}
                            {(assigned_field_auditor || {}).phone_number ? `[${assigned_field_auditor.phone_number}]` : ""}
                          </InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>QC Executive</InfoLabel>
                          <InfoValue>{(assigned_qc_executive || {}).name || "N/A"}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>QC Manager</InfoLabel>
                          <InfoValue>{(assigned_qc_manager || {}).name || "N/A"}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Beneficiary ID</InfoLabel>
                          <InfoValue>{beneficiary_id || propBeneficiaryId || "N/A"}</InfoValue>
                        </InfoItem>
                        {assigned_calling_manager && (
                          <InfoItem>
                            <InfoLabel>Calling Manager</InfoLabel>
                            <InfoValue>{(assigned_calling_manager || {}).name || "N/A"}</InfoValue>
                          </InfoItem>
                        )}
                      </InfoList>
                    </div>
                    <div>
                      <Title style={{ borderBottomColor: '#10b981', visibility: isMobile ? 'hidden' : 'visible' }}>&nbsp;</Title>
                      <InfoList>
                        <InfoItem>
                          <InfoLabel>Field Manager</InfoLabel>
                          <InfoValue>{(assigned_field_manager || {}).name || "N/A"}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Version Number</InfoLabel>
                          <InfoValue>{field_survey_mobile_version_number || "N/A"}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Bulk Uploaded by</InfoLabel>
                          <InfoValue>{bulk_created_by || "N/A"}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Bulk Approved by</InfoLabel>
                          <InfoValue>{bulk_approved_by || "N/A"}</InfoValue>
                        </InfoItem>
                        {assigned_calling_executive && (
                          <InfoItem>
                            <InfoLabel>Calling Executive</InfoLabel>
                            <InfoValue>{(assigned_calling_executive || {}).name || "N/A"}</InfoValue>
                          </InfoItem>
                        )}
                      </InfoList>
                    </div>
                  </DetailBlock>
                </DetailRow>
              )}

              {isEditViewEnable == true && (
                <DetailRow>
                  <DetailBlock>
                    <Block style={{ flex: 1, padding: 0 }}>
                      <Title>Edited Items</Title>
                      <EditRowWrapper>
                        <h3>Field</h3>
                        <h3>Edited From</h3>
                        <h3>Edited To</h3>
                      </EditRowWrapper>

                      {previous_address && (<EditElement name="Address" pkey={addressText} previous_key={previous_address} />)}
                      {previous_phone_number1 && (
                        <EditElement name="Phone Number 1" pkey={phone_number1} previous_key={previous_phone_number1} />)}
                      {previous_phone_number2 && (
                        <EditElement name="Phone Number 2" pkey={phone_number2} previous_key={previous_phone_number2} />)}

                      {(previous_phone_number2 || previous_phone_number1 || previous_address) && (
                        <div
                          style={{
                            'display': 'flex',
                            'justify-content': 'end',
                            marginBottom: 15
                          }}
                        >
                          <Button
                            size="medium"
                            type="submit"
                            borderRadius="5px"
                            bgColor={cinnabar300}
                            onClick={() => handleMetaEdit("reject")}
                          >
                            Reject
                          </Button>
                          <Button
                            size="medium"
                            type="submit"
                            borderRadius="5px"
                            bgColor={green300}
                            onClick={() => handleMetaEdit("accept")}
                            margin="0"
                          >
                            Accept
                          </Button>
                        </div>
                      )}
                    </Block>
                  </DetailBlock>
                </DetailRow>
              )}
            </Fragment>
          )}
        </div>



      </OutletMetaWrapper>

      {
        openUpdateModal ? (
          <UpdateStatusModal
            setModalOpen={openUpdateModal}
            modalAction={() => setUpdateModalOpen(!openUpdateModal)}
            getOutletDetails={getOutletDetails}
            history={history}
            actionList={actionList}
            workflow_permission={workflow_permission}
            selectedOutletRows={[
              (selectedOutletDetails[0] || {}).workflowOutletId,
            ]}
            saveClickedData={saveClickedData}
            selectedCallingTrackerReasonDetails={selectedCallingTrackerReasonDetails}
            setSaveClickedData={setSaveClickedData}
            setWorkflowStateChanged={setWorkflowStateChanged}
            callback={(requestData) => {
              if (isCallingExecutive) {
                uploadCallingTrackerDetails()
              } else if (workflow_state == "qc_pending") {
                statusUpdateCallback(requestData)
              }
            }}
            rsidList={similarOutletData}
          />
        ) : null
      }
      {
        openSimilarOutletModal ? (
          <SimilarOutletModal
            setModalOpen={openUpdateModal}
            modalAction={() => setSimilarOutletModal(!openSimilarOutletModal)}
            getOutletDetails={getOutletDetails}
            history={history}
            similarOutletData={similarOutletData}
            actionList={actionList}
            workflow_permission={workflow_permission}
            selectedOutletRows={[
              (selectedOutletDetails[0] || {}).workflowOutletId,
            ]}
            saveClickedData={saveClickedData}
            selectedCallingTrackerReasonDetails={
              selectedCallingTrackerReasonDetails
            }
            setSaveClickedData={setSaveClickedData}
            setWorkflowStateChanged={setWorkflowStateChanged}
            callback={isCallingExecutive ? uploadCallingTrackerDetails : null}
          />
        ) : null
      }

      {
        isRetailerRequestPending && showPopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px',
                textAlign: 'center',
                width: '35%',
                height: '35%',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                fontSize: '16px',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: '2.5rem',
                  color: '#aaa',
                  marginLeft: '95%',
                  cursor: 'pointer',
                }}
                onClick={() => {

                  setShowPopup(false);
                }}
              >
                &times;
              </span>
              <Error style={{
                fontSize: '3rem',
                paddingTop: '2.5%'
              }} />
              <p style={{
                paddingTop: '1%'
              }}>
                There are {countRetailerRequestPending} Pending Retailer Request!
              </p>
            </div>
          </div>
        )
      }

      {
        galleryImage.length ? (
          <ImageGallery
            images={galleryImage}
            setShowGallery={() => setGalleryImage([])}
          />
        ) : null
      }
    </Fragment >
  );
};